import { Button } from "components/Button";
import { Icon } from "components/Icon";
import { fadeIn } from "react-animations";
import styled, { keyframes } from "styled-components";

// --- Button components ---

interface FilterButtonProps {
  $selected?: boolean;
}

export const FilterButton = styled(Button)<FilterButtonProps>`
  display: flex;
  align-items: center;
  height: 34px;

  padding: 6px;
  border: solid 1px ${(props) => props.theme.grey};
  background-color: ${(props) =>
    props.$selected ? props.theme.primary10 : props.theme.inputBg};
  min-width: max-content;
  justify-content: center;

  font-size: 14px;
  font-weight: bold;
  color: ${(props) => props.theme.black};

  &:hover {
    background-color: ${(props) => props.theme.lightgrey03};
    color: ${(props) => props.theme.black50};
  }
`;

interface FilterSegmentButtonProps {
  $columns?: number;
}

export const FilterSegmentButton = styled(
  FilterButton,
)<FilterSegmentButtonProps>`
  width: calc(100% / ${({ $columns = 3 }) => $columns});
`;

export const FiltersToggleButton = styled(FilterButton)`
  width: 45px;
  height: 42px;
  position: relative;
`;

export const FilterFullWidthButton = styled(FilterButton)`
  flex: 1;
  position: relative;
`;

export const ResetFilterButton = styled(Button)`
  margin: 0 5px;
  padding: 9px 5px;
`;

export const SearchFilterButton = styled(Button)`
  display: flex;
  align-items: center;
  height: 42px;
  padding: 6px 10px;
  border: solid 1px ${(props) => props.theme.grey};
  background-color: ${(props) => props.theme.inputBg};
  min-width: max-content;

  &:hover {
    background-color: ${(props) => props.theme.lightgrey03};
    color: ${(props) => props.theme.black50};
  }

  ${(props) => props.theme.sm`
    margin-left: 5px;
  `};
`;

// --- Icon components ---

export const MenuIcon = styled(Icon)`
  height: 15px;
  width: 6px;
  margin-left: 5px;
  transition: background-color 0.3s;
  background-color: ${(props) => props.theme.black50};
`;

export const FilterIcon = styled(Icon)`
  height: 15px;
  width: 15px;
`;

export const CloseIcon = styled(Icon)`
  height: 12px;
  width: 12px;
  transition: background-color 0.3s;
`;

// --- Layout components ---

export const FilterCounter = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: ${(props) => props.theme.primary};
  border-radius: 8px;
  top: -8px;
  right: -8px;
  color: ${(props) => props.theme.buttonFilledText};
  font-size: 12px;
  line-height: 16px;
`;

export const FiltersContainer = styled.div`
  display: flex;
  height: 34px;
  background-color: ${(props) => props.theme.inputBg};
  align-items: stretch;

  & > * {
    &:nth-child(n + 2) {
      border-left: none;
    }
  }
`;

export const CloseButton = styled.button`
  padding: 5px;

  &:hover ${CloseIcon} {
    background-color: ${(props) => props.theme.black};
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  transition: padding 0.2s ease-in-out;

  & > *:nth-child(n + 1) {
    margin-left: 5px;
  }
`;

export const Content = styled.div`
  flex: 1;
`;

interface ButtonsWrapperProps {
  $isOpen: boolean;
}

export const ButtonsWrapper = styled.div<ButtonsWrapperProps>`
  display: flex;
  align-items: center;

  ${({ theme, $isOpen }) =>
    $isOpen &&
    theme.sm`
    width: 100%;
    justify-content: flex-end;`}
`;

export const FiltersWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: fit-content;
  animation: 0.2s ${keyframes`${fadeIn}`};

  ${(props) => props.theme.sm`
    flex-wrap: wrap;
  `}
`;

export const CloseIconWrapper = styled.div`
  padding: 4px;
  position: absolute;
  right: 6px;
`;

interface SearchWrapperProps {
  $isOpen: boolean;
  $isPanelOpened?: boolean;
}

export const SearchWrapper = styled.div.attrs({
  "data-ignore-panel-close": "",
} as Record<string, string>)<SearchWrapperProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  transition: padding 0.2s ease-in-out;

  ${({ theme, $isOpen }) =>
    $isOpen &&
    theme.sm`
      flex-wrap: wrap;
    `}

  ${({ theme, $isPanelOpened }) =>
    $isPanelOpened &&
    theme.range(
      "padding-right",
      "370px",
      "410px",
      `${theme.breakpoints.lg}px`,
      `${theme.breakpoints.xl}px`,
    )}

  ${({ theme }) => theme.xs`
    padding: 0;
  `}
`;
